import React, { useEffect, useState } from 'react';
import API from '../../api/axiosInstance';
import AddAccreditationCategoryForm from './AddAccreditationCategoryForm';
import EditAccreditationCategoryForm from './EditAccreditationCategoryForm';
import '../css/ParticipantList.css';
import '../css/AccreditationCategoryList.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrashAlt, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import DeleteConfirmModal, { useDeleteConfirm } from './DeleteConfirmModal';
import { GoldLoader } from '../shared/Loaders';

const AccreditationCategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const deleteConfirm = useDeleteConfirm();

  const fetchCategories = async () => {
    try {
      const res = await API.get('/ems/accreditation-categories');
      if (Array.isArray(res.data)) setCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch accreditation categories', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleDelete = (category) => {
    deleteConfirm.ask({
      id: category._id,
      name: category.category_name || 'this accreditation category',
    });
  };

  const confirmDelete = async () => {
    const id = deleteConfirm.pending?.id;
    if (!id) return;
    deleteConfirm.setBusy(true);
    try {
      await API.delete(`/ems/accreditation-categories/${id}`);
      deleteConfirm.clear();
      fetchCategories();
    } catch (err) {
      deleteConfirm.setError(err.response?.data?.message || 'Delete failed');
      deleteConfirm.setBusy(false);
    }
  };

  const filtered = categories.filter((c) => {
    const term = searchTerm.toLowerCase();
    return (
      (c.category_name || '').toLowerCase().includes(term) ||
      (c.function || '').toLowerCase().includes(term) ||
      (c.code || '').toLowerCase().includes(term)
    );
  });

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const sorted = sortConfig.key
    ? [...filtered].sort((a, b) => {
        const aVal = (a[sortConfig.key] || '').toLowerCase();
        const bVal = (b[sortConfig.key] || '').toLowerCase();
        const result = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? result : -result;
      })
    : filtered;

  const effectivePerPage = itemsPerPage >= sorted.length ? Math.max(sorted.length, 1) : itemsPerPage;
  const totalPages = Math.max(Math.ceil(sorted.length / effectivePerPage), 1);
  const indexOfLast = currentPage * effectivePerPage;
  const indexOfFirst = indexOfLast - effectivePerPage;
  const currentItems = sorted.slice(indexOfFirst, indexOfLast);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const getVisiblePages = () => {
    if (totalPages <= 5) return pageNumbers;
    if (currentPage <= 3) return pageNumbers.slice(0, 5);
    if (currentPage >= totalPages - 2) return pageNumbers.slice(-5);
    return pageNumbers.slice(currentPage - 3, currentPage + 2);
  };

  if (loading) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="ems-accreditation-categories-page p-4">
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 ems-list-toolbar">
        <div className="d-flex align-items-center w-25">
          <div className="me-2" style={{ flex: '1' }}>
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search accreditation categories"
              style={{ border: '1px solid #d1cfcf' }}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          style={{
            background: '#000',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '5px',
            border: 'none',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          + Add Category
        </button>
      </div>

      <div className="accreditation-category-header">
        <div
          className={`sort-header ${sortConfig.key === 'category_name' ? 'sorted' : ''}`}
          onClick={() => toggleSort('category_name')}
        >
          Category <FontAwesomeIcon icon={sortIcon('category_name')} className="sort-icon" />
        </div>
        <div
          className={`sort-header ${sortConfig.key === 'function' ? 'sorted' : ''}`}
          onClick={() => toggleSort('function')}
        >
          Function <FontAwesomeIcon icon={sortIcon('function')} className="sort-icon" />
        </div>
        <div
          className={`sort-header ${sortConfig.key === 'code' ? 'sorted' : ''}`}
          onClick={() => toggleSort('code')}
        >
          Code <FontAwesomeIcon icon={sortIcon('code')} className="sort-icon" />
        </div>
        <div>Actions</div>
      </div>

      {loading ? (
        <GoldLoader label="Loading..." variant="section" />
      ) : filtered.length === 0 ? (
        <div className="p-4 text-center text-muted">No accreditation categories available</div>
      ) : (
        currentItems.map((c) => (
          <div className="accreditation-category-row" key={c._id}>
            <div>{c.category_name || '—'}</div>
            <div>{c.function || '—'}</div>
            <div>{c.code || '—'}</div>
            <div className="accreditation-category-actions">
              <button
                type="button"
                className="btn btn-sm action-btn"
                onClick={() => setCategoryToEdit(c)}
                title="Edit accreditation category"
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
              <button
                type="button"
                className="btn btn-sm action-btn"
                onClick={() => handleDelete(c)}
                title="Delete accreditation category"
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </button>
            </div>
          </div>
        ))
      )}

      {sorted.length > 0 && (
        <div className="participant-pagination">
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: '13px', color: '#666' }}>Show</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', fontSize: '13px' }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="pagination-box">
            <button
              type="button"
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>

            {getVisiblePages().map((num) => (
              <button
                key={num}
                type="button"
                className={`pagination-btn ${num === currentPage ? 'active' : ''}`}
                onClick={() => setCurrentPage(num)}
              >
                {num}
              </button>
            ))}

            <button
              type="button"
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showAddModal && (
        <AddAccreditationCategoryForm onClose={() => setShowAddModal(false)} onAdded={fetchCategories} />
      )}

      {categoryToEdit && (
        <EditAccreditationCategoryForm
          onClose={() => setCategoryToEdit(null)}
          onUpdated={fetchCategories}
          categoryToEdit={categoryToEdit}
        />
      )}
      {deleteConfirm.pending && (
        <DeleteConfirmModal
          name={deleteConfirm.pending.name}
          error={deleteConfirm.error}
          busy={deleteConfirm.busy}
          onConfirm={confirmDelete}
          onCancel={deleteConfirm.clear}
        />
      )}
    </div>
  );
};

export default AccreditationCategoryList;
